import { useState, type Ref } from "react";
import type { DiscountInput, PricedCatalogueItem } from "@probetalab/contracts";
import { get } from "../lib/api.js";
import { money, parseMoney } from "../lib/format.js";
import { useDebounced, useQuery } from "../lib/useQuery.js";
import { Icon } from "./icons.js";
import { Field, IconButton, Input, Money, Segmented } from "./ui.js";
import { NO_AUTOFILL } from "../lib/noAutofill.js";

/** A chosen study or package with the price the screen showed (the server snapshots and flags a mismatch). */
export type Line = PricedCatalogueItem;

/** Search the catalogue by clave or name; packages are listed first when they match. */
export function StudySearch({
  onPick,
  selected,
  autoFocus,
  studiesOnly = false,
  onInfo,
  inputRef,
  branchId,
  priceListId,
}: {
  onPick: (item: PricedCatalogueItem) => void;
  selected: Line[];
  autoFocus?: boolean;
  studiesOnly?: boolean;
  /** When given, every result gets a «Ver detalle» button beside it. */
  onInfo?: (item: PricedCatalogueItem) => void;
  inputRef?: Ref<HTMLInputElement>;
  /** Price with this branch's list (the server falls back to the default list). */
  branchId?: string;
  /** Price with this list; wins over `branchId` (an existing order keeps its own list). */
  priceListId?: string | null;
}) {
  const [q, setQ] = useState("");
  const dq = useDebounced(q.trim());
  const pricing = priceListId ? { price_list_id: priceListId } : branchId ? { branch_id: branchId } : {};
  const pricingKey = priceListId ?? branchId ?? "";
  const query = useQuery(() => get("catalogueBrowse", { query: { q: dq, kind: studiesOnly ? "study" : "all", limit: 30, ...pricing } }), [dq, studiesOnly, pricingKey], { enabled: dq.length >= 2 });
  const chosen = new Set(selected.map((l) => `${l.kind}:${l.id}`));
  // Nothing listed until the lab types (Fernanda, 2026-11-02 20:48: "que salga en blanco hasta que
  // seleccionemos los estudios"); packages still come up in the search results.
  const items = dq.length >= 2 ? (query.data?.items ?? []) : [];
  const sorted = [...items].sort((a, b) => (a.kind === b.kind ? 0 : a.kind === "package" ? -1 : 1));

  return (
    <div className="stack">
      <div className="input-wrap">
        <Icon name="search" />
        <input {...NO_AUTOFILL}
          ref={inputRef}
          type="search"
          className="input lg"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Clave o nombre del estudio (BH, glucosa, perfil…)"
          aria-label="Buscar estudio"
          autoFocus={autoFocus}
          onKeyDown={(e) => {
            if (e.key === "Enter" && sorted.length === 1 && sorted[0]) {
              e.preventDefault();
              onPick(sorted[0]);
              setQ("");
            }
          }}
        />
      </div>
      {dq.length >= 2 && (
      <div className="study-results">
        {sorted.length === 0 && dq.length >= 2 && <div className="list-row faint">{query.loading ? "Buscando…" : "Sin resultados en el catálogo."}</div>}
        {sorted.map((it) => {
          const isChosen = chosen.has(`${it.kind}:${it.id}`);
          // A package whose studies are not defined yet is shown, so the lab sees it exists, but cannot be picked.
          const undefinedPackage = it.kind === "package" && it.study_claves.length === 0;
          const hit = (
            <button
              key={`${it.kind}:${it.id}`}
              type="button"
              className={`study-hit ${it.kind === "package" ? "pkg" : ""}`}
              disabled={isChosen || it.amount_cents === null || undefinedPackage}
              onClick={() => {
                onPick(it);
                setQ("");
              }}
              style={isChosen ? { opacity: 0.5 } : undefined}
            >
              <span className="clave">{it.clave}</span>
              <span>
                <div className="name">{it.name}</div>
                <div className="meta">
                  {it.kind === "package" ? (undefinedPackage ? "Paquete sin estudios asignados" : `Paquete · ${it.study_claves.join(", ")}`) : (it.area_name ?? "Sin área")}
                  {it.indications ? ` · ${it.indications}` : ""}
                </div>
              </span>
              <span className="row" style={{ gap: 12 }}>
                {it.container_colour_hex && <span className="tube-cap" style={{ background: it.container_colour_hex }} title="Recipiente" />}
                {undefinedPackage ? <span className="faint small">Sin estudios</span> : it.amount_cents === null ? <span className="faint small">Sin precio</span> : <Money cents={it.amount_cents} />}
                {isChosen ? <Icon name="check" style={{ color: "var(--brand)" }} /> : undefinedPackage ? <span style={{ width: 24 }} /> : <Icon name="plus" style={{ color: "var(--ink-3)" }} />}
              </span>
            </button>
          );
          if (!onInfo) return hit;
          // The info button sits beside the hit, not inside it: a button cannot contain a button.
          return (
            <div key={`${it.kind}:${it.id}`} className="study-hit-row">
              {hit}
              <IconButton icon="info" label={`Ver detalle de ${it.name}`} size="sm" onClick={() => onInfo(it)} />
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

export function LinesList({ lines, onRemove, onInfo }: { lines: Line[]; onRemove?: (l: Line) => void; onInfo?: (l: Line) => void }) {
  if (!lines.length) return <div className="list-row faint">Aún no hay estudios en la solicitud.</div>;
  return (
    <div className="lines">
      {lines.map((l) => (
        <div key={`${l.kind}:${l.id}`} className="line">
          <div>
            <div className="name">
              <span className="mono muted" style={{ marginRight: 8 }}>
                {l.clave}
              </span>
              {l.name}
            </div>
            <div className="meta">{l.kind === "package" ? "Paquete" : (l.area_name ?? "")}</div>
          </div>
          <Money cents={l.amount_cents ?? 0} />
          {onRemove || onInfo ? (
            <span className="row" style={{ gap: 4 }}>
              {onInfo && <IconButton icon="info" label={`Ver detalle de ${l.name}`} size="sm" onClick={() => onInfo(l)} />}
              {onRemove && <IconButton icon="x" label={`Quitar ${l.name}`} size="sm" onClick={() => onRemove(l)} />}
            </span>
          ) : (
            <span />
          )}
        </div>
      ))}
    </div>
  );
}

export interface DiscountState {
  kind: "percent" | "fixed";
  /** Raw text as typed. */
  text: string;
  reason: string;
}
export const NO_DISCOUNT: DiscountState = { kind: "percent", text: "", reason: "" };

/** Both figures are derived from one: type a percent and see the pesos, or type pesos and see the percent. */
export function computeDiscount(subtotal: number, d: DiscountState): { amount: number; percent: number; input: DiscountInput | null } {
  if (d.kind === "percent") {
    const pct = Math.min(100, Math.max(0, Math.round(Number(d.text) || 0)));
    const amount = Math.round((subtotal * pct) / 100);
    return { amount, percent: pct, input: pct > 0 ? { kind: "percent", value: pct, reason: d.reason.trim() || null } : null };
  }
  const cents = Math.min(subtotal, parseMoney(d.text) ?? 0);
  const percent = subtotal > 0 ? Math.round((cents / subtotal) * 100) : 0;
  return { amount: cents, percent, input: cents > 0 ? { kind: "fixed", value: cents, reason: d.reason.trim() || null } : null };
}

export function DiscountEditor({ subtotal, value, onChange }: { subtotal: number; value: DiscountState; onChange: (d: DiscountState) => void }) {
  const { amount, percent } = computeDiscount(subtotal, value);
  return (
    <div className="stack">
      <div className="row between wrap">
        <span className="label" style={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--ink-2)" }}>
          Descuento
        </span>
        <Segmented small value={value.kind} onChange={(k) => onChange({ ...value, kind: k, text: "" })} options={[{ value: "percent", label: "Porcentaje" }, { value: "fixed", label: "Monto" }]} />
      </div>
      <div className="grid-2">
        <div className="input-wrap">
          <span className="suffix">{value.kind === "percent" ? "%" : "MXN"}</span>
          <Input inputMode="decimal" value={value.text} onChange={(e) => onChange({ ...value, text: e.target.value })} placeholder={value.kind === "percent" ? "0" : "0.00"} aria-label={value.kind === "percent" ? "Porcentaje de descuento" : "Monto de descuento"} />
        </div>
        <Input value={value.reason} onChange={(e) => onChange({ ...value, reason: e.target.value })} placeholder="Motivo (promoción, convenio…)" aria-label="Motivo del descuento" />
      </div>
      {amount > 0 && (
        <p className="small muted">
          Equivale a {value.kind === "percent" ? money(amount) : `${percent} %`}.
        </p>
      )}
    </div>
  );
}

export function Totals({ subtotal, discount, total, paid, change, children }: { subtotal: number; discount: number; total: number; paid?: number; change?: number; children?: React.ReactNode }) {
  const balance = paid !== undefined ? Math.max(0, total - paid) : undefined;
  return (
    <div className="totals">
      <div className="trow">
        <span>Subtotal</span>
        <Money cents={subtotal} />
      </div>
      {discount > 0 && (
        <div className="trow">
          <span>Descuento</span>
          <span className="money">− {money(discount)}</span>
        </div>
      )}
      <div className="trow total">
        <span>Total</span>
        <Money cents={total} />
      </div>
      {paid !== undefined && (
        <div className="trow">
          <span>Pagado</span>
          <Money cents={Math.min(paid, total)} />
        </div>
      )}
      {balance !== undefined && balance > 0 && (
        <div className="trow due">
          <span>Adeudo</span>
          <Money cents={balance} />
        </div>
      )}
      {change !== undefined && change > 0 && (
        <div className="trow change">
          <span>Cambio</span>
          <Money cents={change} />
        </div>
      )}
      {children}
    </div>
  );
}

export { Field as LinesField };
